/* Invoice sub-panel — EXPORT INVOICE PDFs made straight from the packing slip. */
import { DocAcksBanner } from "@/components/DocAcksBanner";
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Eye, FileText, Loader2, Printer, RefreshCw, Search, Trash2, Upload } from "lucide-react";
import { BulkInvoiceBar, ExportPackButton, MissingInvoicesCard, PrintedStamp, ShippingCell, confirmReprint, printPdfUrl } from "@/components/InvoiceDocExtras";
import { DailyPrintReportCard } from "@/components/DailyPrintReportCard";
import { ConsigneeCell, ConsigneeRules } from "@/components/InvoiceConsignee";
import { COURIER_RX, printAdc } from "@/components/AdcDocPanel";

const openPdf = async (id, mode) => {
  const r = await axios.get(`${API}/invoice-doc/history/${id}/download`, { headers: getAuthHeader(), responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  if (mode === "view") { window.open(url, "_blank"); return; }
  const a = document.createElement("a");
  a.href = url; a.download = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || "invoice.pdf";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const HsnSetting = ({ isAdmin }) => {
  const [hsn, setHsn] = useState("");
  useEffect(() => { axios.get(`${API}/invoice-doc/settings`, { headers: getAuthHeader() }).then(r => setHsn(r.data.hsn)).catch(() => {}); }, []);
  const save = async () => {
    try { await axios.put(`${API}/invoice-doc/settings`, { hsn }, { headers: getAuthHeader() }); toast.success("Default HSN saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="flex items-center gap-2 text-xs text-slate-600">
      Default HSN
      <Input value={hsn} onChange={(e) => setHsn(e.target.value)} disabled={!isAdmin} className="h-8 w-28" data-testid="inv-hsn-input" />
      {isAdmin && <Button size="sm" variant="outline" className="h-8" onClick={save} data-testid="inv-hsn-save">Save</Button>}
    </div>
  );
};

export default function InvoiceDocPanel({ user }) {
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [printed, setPrinted] = useState("");
  const [busy, setBusy] = useState("");
  const [sel, setSel] = useState(new Set());
  const [tick, setTick] = useState(0);
  const toggle = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const fileRef = useRef(null);
  const load = useCallback(() => {
    axios.get(`${API}/invoice-doc/history`, { params: { q, printed }, headers: getAuthHeader() })
      .then(r => setRows(r.data)).catch(e => toast.error(e.response?.data?.detail || "Could not load invoices"));
  }, [q, printed]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const upload = async (f) => {
    if (!f) return;
    setBusy("upload");
    const fd = new FormData(); fd.append("packing_slip", f);
    try {
      const r = await axios.post(`${API}/invoice-doc/generate`, fd, { headers: getAuthHeader() });
      toast.success(`Invoice ${r.data.invoice_no} created — ${r.data.products_count} products, $${r.data.total}`);
      load(); setTick(t => t + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the invoice"); }
    setBusy(""); if (fileRef.current) fileRef.current.value = "";
  };
  const printOne = async (r) => {
    if (!confirmReprint(r.last_print, r.invoice_no)) return;
    const res = await axios.get(`${API}/invoice-doc/pack-print/${encodeURIComponent(r.invoice_no)}`,
      { headers: getAuthHeader(), responseType: "blob", params: { copies: "invoice:1" } });
    printPdfUrl(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })));
    load();
  };
  const act = async (id, kind) => {
    setBusy(id + kind);
    try {
      if (kind === "regen") { await axios.post(`${API}/invoice-doc/history/${id}/regenerate`, {}, { headers: getAuthHeader() }); toast.success("Invoice regenerated with latest company details"); load(); }
      else if (kind === "del") { if (window.confirm("Delete this invoice?")) { await axios.delete(`${API}/invoice-doc/history/${id}`, { headers: getAuthHeader() }); load(); } }
      else if (kind === "print") await printOne(rows.find(x => x.id === id));
      else await openPdf(id, kind);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    setBusy("");
  };

  return (
    <div className="space-y-5" data-testid="invoice-doc-panel">
      <DocAcksBanner />
      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800"><FileText className="h-5 w-5 text-indigo-600" /> Tax Invoice</h2>
            <p className="text-sm text-slate-500">Made automatically when a packing slip is uploaded in Order Register. Same products, qty and amounts as the slip; company picked from its GSTIN.</p>
          </div>
          <HsnSetting isAdmin={isAdmin} />
          {isAdmin && <ConsigneeRules />}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="inv-upload-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy === "upload"} className="bg-indigo-600 hover:bg-indigo-700" data-testid="inv-upload-btn">
            {busy === "upload" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />} Upload packing slip
          </Button>
          <span className="text-xs text-slate-400">Manual upload — re-uploading the same invoice number replaces it.</span>
        </div>
      </div>
      <MissingInvoicesCard refreshKey={tick} onMade={load} />
      <DailyPrintReportCard refreshKey={rows} onPrinted={load} isAdmin={isAdmin} />
      <div className="rounded-xl border bg-white p-5 shadow-sm">
        <BulkInvoiceBar selected={sel} onClear={() => setSel(new Set())} onPrinted={load} />
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h3 className="font-semibold text-slate-700">Invoices</h3>
          <select value={printed} onChange={(e) => setPrinted(e.target.value)} className="ml-auto h-9 rounded-md border px-2 text-xs" data-testid="inv-printed-filter">
            <option value="">All invoices</option><option value="no">Not printed</option><option value="yes">Printed</option>
          </select>
          <div className="relative w-72">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice, buyer, company" className="h-9 pl-8" data-testid="inv-search" />
          </div>
        </div>
        {!rows ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400" data-testid="inv-empty">No invoices yet — they appear here after a packing slip upload.</p>
        ) : (
          <table className="doc-cards w-full text-sm" data-testid="inv-table">
            <thead><tr className="border-b text-left text-xs text-slate-500">
              <th className="w-8 py-2"><input type="checkbox" checked={rows.length > 0 && rows.every(r => sel.has(r.id))}
                onChange={(e) => setSel(e.target.checked ? new Set(rows.map(r => r.id)) : new Set())} data-testid="inv-select-all" /></th>
              <th className="py-2">Invoice</th><th>Date</th><th>Buyer</th><th>Method</th><th>Company</th><th className="text-right">Products</th><th className="text-right">Products $</th><th className="text-center">Shipping $</th><th className="pr-4 text-right">Grand total $</th><th>Source</th><th>Made</th><th>Printed</th><th className="text-right">Actions</th>
            </tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`inv-row-${r.invoice_no}`}>
                <td data-label="Select"><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} data-testid={`inv-select-${r.invoice_no}`} /></td>
                <td data-label="Invoice" className="py-2 font-mono font-semibold text-indigo-700">{r.invoice_no}</td>
                <td data-label="Date">{r.invoice_date}</td><td data-label="Buyer" className="max-w-[180px] truncate" title={r.buyer_address || ""}>{r.buyer}</td><td data-label="Method" data-testid={`inv-method-${r.invoice_no}`}>{r.method ? <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">{r.method}</span> : "—"}<ConsigneeCell r={r} onDone={load} /></td><td data-label="Company">{r.company_name}</td>
                <td data-label="Products" className="text-right">{r.products_count}</td><td data-label="Products $" className="text-right">{Number(r.total).toFixed(2)}</td>
                <td data-label="Shipping $" className="text-center"><ShippingCell r={r} onSaved={(d) => setRows(rs => rs.map(x => (x.id === d.id ? d : x)))} /></td>
                <td data-label="Grand total $" className="pr-4 text-right font-semibold" data-testid={`inv-grand-${r.invoice_no}`}>{Number(r.grand_total ?? r.total).toFixed(2)}</td>
                <td data-label="Source"><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${r.source === "order_register" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{r.source === "order_register" ? "AUTO" : "MANUAL"}</span></td>
                <td data-label="Created" className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                <td data-label="Printed"><PrintedStamp r={r} /></td>
                <td data-label="Actions" className="whitespace-nowrap text-right">
                  {[["view", Eye, "View"], ["dl", Download, "Download"], ["print", Printer, "Print this invoice (1 copy)"], ["regen", RefreshCw, "Regenerate with latest company details"]].map(([k, I, t]) => (
                    <Button key={k} variant="ghost" size="sm" className="h-7 w-7 p-0" title={t} disabled={!!busy} onClick={() => act(r.id, k)} data-testid={`inv-${k}-${r.invoice_no}`}>
                      {busy === r.id + k ? <Loader2 className="h-4 w-4 animate-spin" /> : <I className="h-4 w-4" />}
                    </Button>))}
                  {COURIER_RX.test(r.method || "") && <Button variant="ghost" size="sm" className="h-7 px-1.5 text-[11px] font-semibold text-emerald-700" title="Print ADC sheet" disabled={!!busy} onClick={() => printAdc(r.invoice_no)} data-testid={`inv-adc-${r.invoice_no}`}>ADC</Button>}
                  <span className="hidden md:inline-block"><ExportPackButton invoiceNo={r.invoice_no} lastPrint={r.last_print} onPrinted={load} /></span>
                  {isAdmin && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600" disabled={!!busy} onClick={() => act(r.id, "del")} data-testid={`inv-del-${r.invoice_no}`}><Trash2 className="h-4 w-4" /></Button>}
                </td>
                <td data-label="Export pack" className="md:!hidden"><ExportPackButton invoiceNo={r.invoice_no} big lastPrint={r.last_print} onPrinted={load} /></td>
              </tr>))}</tbody>
          </table>)}
      </div>
    </div>
  );
}
